import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, Cell, AreaChart, Area
} from 'recharts';
import {
  TrendingUp, Users, Clock, Map,
  Filter, RefreshCw, ChevronDown,
  Phone, Percent, Eye, Calendar,
  ArrowLeft, Download, Share2, 
  Info, AlertCircle, Building,
  BarChart2, PieChart as PieChartIcon,
  User, GlobeIcon, Smartphone,
  ArrowDown, ArrowUp
} from 'lucide-react';

// Componente para tarjetas de estadísticas
const StatCard = ({ title, value, icon: Icon, trend, color = "blue", onClick }) => {
  const colors = {
    blue: { bg: "bg-blue-50", text: "text-blue-600", trend: { up: "text-blue-600", down: "text-red-500" } },
    green: { bg: "bg-green-50", text: "text-green-600", trend: { up: "text-green-600", down: "text-red-500" } },
    purple: { bg: "bg-purple-50", text: "text-purple-600", trend: { up: "text-purple-600", down: "text-red-500" } },
    pink: { bg: "bg-pink-50", text: "text-pink-600", trend: { up: "text-pink-600", down: "text-red-500" } },
    indigo: { bg: "bg-indigo-50", text: "text-indigo-600", trend: { up: "text-indigo-600", down: "text-red-500" } },
    amber: { bg: "bg-amber-50", text: "text-amber-600", trend: { up: "text-amber-600", down: "text-red-500" } }
  };
  
  const colorClasses = colors[color] || colors.blue;
  
  return (
    <div 
      className={`bg-white rounded-xl shadow-sm border border-gray-100 p-5 
                transition-all duration-300 hover:shadow-md ${onClick ? 'cursor-pointer' : ''}`}
      onClick={onClick}
    >
      <div className="flex items-center justify-between mb-3">
        <div className={`p-2 rounded-lg ${colorClasses.bg}`}>
          <Icon className={`w-5 h-5 ${colorClasses.text}`} />
        </div>
        {trend !== undefined && (
          <div className={`flex items-center gap-1 text-sm font-medium ${trend >= 0 
            ? colorClasses.trend.up 
            : colorClasses.trend.down}`}
          >
            {trend >= 0 ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
            {Math.abs(trend)}%
          </div>
        )}
      </div>
      <h3 className="text-gray-500 text-sm mb-1">{title}</h3>
      <p className="text-2xl font-bold text-gray-800">{value}</p>
    </div>
  );
};

// Componente para pestañas de filtros
const TabButton = ({ active, onClick, children, icon: Icon }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
      active 
        ? 'bg-blue-600 text-white shadow-sm' 
        : 'bg-white text-gray-600 hover:bg-gray-50'
    }`}
  >
    {Icon && <Icon className="w-4 h-4" />}
    {children}
  </button>
);

// Componente para la tarjeta del gráfico
const ChartCard = ({ title, icon: Icon, color = "blue", children, tooltip }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {Icon && (
            <div className={`p-1.5 rounded-md bg-${color}-50 text-${color}-600`}>
              <Icon className="w-4 h-4" />
            </div>
          )}
          <h3 className="font-medium text-gray-800">{title}</h3>
        </div>
        {tooltip && (
          <div className="relative">
            <button 
              className="p-1 text-gray-400 hover:text-gray-600 rounded-full"
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
            >
              <Info className="w-4 h-4" />
            </button>
            {showTooltip && (
              <div className="absolute right-0 top-full mt-2 w-64 p-3 bg-gray-800 text-white text-xs rounded-lg z-10">
                {tooltip}
                <div className="absolute top-0 right-2 transform -translate-y-1/2 rotate-45 w-2 h-2 bg-gray-800"></div>
              </div>
            )}
          </div>
        )}
      </div>
      <div className="p-4">
        {children}
      </div>
    </div>
  );
};

// Componente de página completo
const StatisticsPage = () => {
  const { user } = useAuth();
  const { propertyId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [timeFilter, setTimeFilter] = useState('month');
  const [chartType, setChartType] = useState('line');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [propertyInfo, setPropertyInfo] = useState(null);
  
  const COLORS = ['#3B82F6', '#8B5CF6', '#EC4899', '#10B981', '#F59E0B', '#6B7280'];
  const isPropertySpecific = !!propertyId;

  useEffect(() => {
    fetchStatistics();
  }, [timeFilter, propertyId]);

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      let endpoint;
      
      if (isPropertySpecific) {
        endpoint = `https://backend-production-1786.up.railway.app/api/statistics/property/${propertyId}`;
      } else {
        endpoint = `https://backend-production-1786.up.railway.app/api/statistics?timeframe=${timeFilter}`;
      }
      
      const response = await fetch(endpoint, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error('Error al cargar estadísticas');
      
      const data = await response.json();
      setStats(data);

      // Si es una propiedad específica, obtenemos también la información básica de la propiedad
      if (isPropertySpecific) {
        const propResponse = await fetch(`http://localhost:3001/api/propiedades/${propertyId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (propResponse.ok) {
          const propData = await propResponse.json();
          setPropertyInfo(propData);
        }
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error:', error);
      setLoading(false);
    }
  };

  const handleExportData = () => {
    // Implementación de exportación de datos
    alert('Exportación de datos próximamente disponible');
  };

  const handleShareReport = () => {
    // Implementación de compartir informe
    alert('Compartir informe próximamente disponible');
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const LoadingSpinner = () => (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="relative">
        <div className="w-12 h-12 rounded-full border-2 border-gray-200"></div>
        <div className="absolute top-0 left-0 w-12 h-12 rounded-full border-2 border-t-blue-600 border-r-transparent border-b-transparent border-l-transparent animate-spin"></div>
      </div>
    </div>
  );

  if (loading) {
    return <LoadingSpinner />;
  }

  // Renderizado de estadísticas específicas de una propiedad
  if (isPropertySpecific && stats) {
    return (
      <div className="bg-gray-50 min-h-screen py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          {/* Header with back button */}
          <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <button 
                onClick={handleGoBack}
                className="p-2 rounded-lg bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-800">Estadísticas de la Propiedad</h1>
                <p className="text-gray-600">{propertyInfo?.titulo || 'Propiedad'}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <button 
                onClick={handleExportData}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 text-sm font-medium"
              >
                <Download className="w-4 h-4" />
                Exportar
              </button>
              <button 
                onClick={handleShareReport}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 rounded-lg text-white hover:bg-blue-700 text-sm font-medium"
              >
                <Share2 className="w-4 h-4" />
                Compartir
              </button>
            </div>
          </div>

          {/* Filter Options */}
          <div className="mb-6 flex flex-wrap items-center justify-between bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <div className="flex gap-2 mb-4 sm:mb-0">
              <TabButton 
                active={timeFilter === 'week'} 
                onClick={() => setTimeFilter('week')}
                icon={Calendar}
              >
                Última Semana
              </TabButton>
              <TabButton 
                active={timeFilter === 'month'} 
                onClick={() => setTimeFilter('month')}
                icon={Calendar}
              >
                Último Mes
              </TabButton>
              <TabButton 
                active={timeFilter === 'year'} 
                onClick={() => setTimeFilter('year')}
                icon={Calendar}
              >
                Último Año
              </TabButton>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setChartType('line')}
                className={`p-2 rounded-lg ${chartType === 'line' ? 'bg-blue-50 text-blue-600' : 'text-gray-500 hover:bg-gray-50'}`}
              >
                <TrendingUp className="w-5 h-5" />
              </button>
              <button
                onClick={() => setChartType('bar')}
                className={`p-2 rounded-lg ${chartType === 'bar' ? 'bg-blue-50 text-blue-600' : 'text-gray-500 hover:bg-gray-50'}`}
              >
                <BarChart2 className="w-5 h-5" />
              </button>
              <button
                onClick={() => setChartType('pie')}
                className={`p-2 rounded-lg ${chartType === 'pie' ? 'bg-blue-50 text-blue-600' : 'text-gray-500 hover:bg-gray-50'}`}
              >
                <PieChartIcon className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
            <StatCard
              title="Visitas Totales"
              value={stats.totalVisits || 0}
              icon={Eye}
              trend={stats?.trends?.visitsChange}
              color="blue"
            />
            <StatCard
              title="Tiempo Promedio"
              value={`${Math.round(stats.visitTimes?.averageDuration || 0)}s`}
              icon={Clock}
              color="purple"
            />
            <StatCard
              title="Clicks de Contacto"
              value={stats.summary?.contactClicks || 0}
              icon={Phone}
              trend={stats?.trends?.contactClicksChange}
              color="green"
            />
            <StatCard
              title="Tasa de Conversión"
              value={`${(stats.summary?.conversionRate || 0).toFixed(1)}%`}
              icon={Percent}
              trend={stats?.trends?.conversionRateChange}
              color="amber"
            />
          </div>

          {/* Charts Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Daily Visits Trend */}
            <ChartCard 
              title="Tendencia de Visitas Diarias" 
              icon={TrendingUp}
              tooltip="Muestra la evolución diaria de las visitas a la propiedad durante el período seleccionado."
            >
              <ResponsiveContainer width="100%" height={300}>
                {chartType === 'line' ? (
                  <LineChart data={stats.visitTrends?.daily || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f1f1" />
                    <XAxis dataKey="date" stroke="#718096" />
                    <YAxis stroke="#718096" />
                    <Tooltip contentStyle={{ borderRadius: '8px' }} />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="visits" 
                      name="Visitas"
                      stroke="#3B82F6" 
                      strokeWidth={2}
                      activeDot={{ r: 8 }}
                      dot={{ fill: '#3B82F6', r: 4 }}
                    />
                  </LineChart>
                ) : chartType === 'bar' ? (
                  <BarChart data={stats.visitTrends?.daily || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f1f1" />
                    <XAxis dataKey="date" stroke="#718096" />
                    <YAxis stroke="#718096" />
                    <Tooltip contentStyle={{ borderRadius: '8px' }} />
                    <Legend />
                    <Bar dataKey="visits" name="Visitas" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                ) : (
                  <AreaChart data={stats.visitTrends?.daily || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f1f1" />
                    <XAxis dataKey="date" stroke="#718096" />
                    <YAxis stroke="#718096" />
                    <Tooltip contentStyle={{ borderRadius: '8px' }} />
                    <Legend />
                    <defs>
                      <linearGradient id="colorVisits" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.8}/>
                        <stop offset="95%" stopColor="#3B82F6" stopOpacity={0.1}/>
                      </linearGradient>
                    </defs>
                    <Area 
                      type="monotone" 
                      dataKey="visits" 
                      name="Visitas"
                      stroke="#3B82F6" 
                      fillOpacity={1}
                      fill="url(#colorVisits)" 
                    />
                  </AreaChart>
                )}
              </ResponsiveContainer>
            </ChartCard>

            {/* Hourly Distribution */}
            <ChartCard 
              title="Distribución Horaria de Visitas" 
              icon={Clock}
              color="purple"
              tooltip="Muestra las horas del día en que la propiedad recibe más visitas."
            >
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={stats.visitTrends?.hourly || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f1f1" />
                  <XAxis dataKey="hour" stroke="#718096" />
                  <YAxis stroke="#718096" />
                  <Tooltip contentStyle={{ borderRadius: '8px' }} />
                  <Legend />
                  <Bar dataKey="visits" name="Visitas" fill="#8B5CF6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            {/* Demographics - Age Groups */}
            <ChartCard 
              title="Distribución por Edad" 
              icon={User}
              color="green"
              tooltip="Muestra la distribución de edades de los visitantes interesados en la propiedad."
            >
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={stats.demographics?.ageGroups || []}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                    nameKey="group"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  >
                    {(stats.demographics?.ageGroups || []).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [`${value}`, 'Visitantes']} />
                  <Legend layout="vertical" verticalAlign="middle" align="right" />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>

            {/* Demographics - Gender */}
            <ChartCard 
              title="Distribución por Género" 
              icon={User}
              color="pink"
              tooltip="Muestra la distribución por género de los visitantes interesados en la propiedad."
            >
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={stats.demographics?.gender || []}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                    nameKey="gender"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  >
                    {(stats.demographics?.gender || []).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [`${value}`, 'Visitantes']} />
                  <Legend layout="vertical" verticalAlign="middle" align="right" />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>

            {/* Geographic Distribution */}
            <ChartCard 
              title="Distribución Geográfica" 
              icon={GlobeIcon}
              color="indigo"
              tooltip="Muestra la distribución geográfica de los visitantes interesados en la propiedad."
              className="col-span-1 lg:col-span-2"
            >
              <ResponsiveContainer width="100%" height={300}>
                <BarChart layout="vertical" data={stats.geographicDistribution || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f1f1" horizontal={false} />
                  <XAxis type="number" stroke="#718096" />
                  <YAxis dataKey="location" type="category" width={120} stroke="#718096" />
                  <Tooltip contentStyle={{ borderRadius: '8px' }} />
                  <Legend />
                  <Bar dataKey="visits" name="Visitas" radius={[0, 4, 4, 0]}>
                    {(stats.geographicDistribution || []).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          {/* Device Distribution */}
          <div className="mb-8">
            <ChartCard 
              title="Distribución por Dispositivos" 
              icon={Smartphone}
              color="blue"
              tooltip="Muestra los diferentes dispositivos desde los que los usuarios visitan la propiedad."
            >
              <div className="h-[300px] flex items-center justify-center">
                <div className="text-gray-500 italic">
                  Datos por dispositivo próximamente disponibles
                </div>
              </div>
            </ChartCard>
          </div>

          {/* Recent Visitors Table */}
          <div className="mb-8">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 rounded-md bg-blue-50 text-blue-600">
                    <Users className="w-4 h-4" />
                  </div>
                  <h3 className="font-medium text-gray-800">Visitantes Recientes</h3>
                </div>
                <div className="text-xs text-gray-500">
                  Últimas 24 horas
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <th className="px-5 py-3 border-b-2 border-gray-100">Fecha</th>
                      <th className="px-5 py-3 border-b-2 border-gray-100">Hora</th>
                      <th className="px-5 py-3 border-b-2 border-gray-100">Ubicación</th>
                      <th className="px-5 py-3 border-b-2 border-gray-100">Género</th>
                      <th className="px-5 py-3 border-b-2 border-gray-100">Edad</th>
                      <th className="px-5 py-3 border-b-2 border-gray-100">Duración</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 text-sm text-gray-700">
                    {(stats.recentVisitors || []).length > 0 ? (
                      (stats.recentVisitors || []).map((visitor, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-5 py-3">{visitor.date}</td>
                          <td className="px-5 py-3">{visitor.time}</td>
                          <td className="px-5 py-3">{visitor.location}</td>
                          <td className="px-5 py-3">{visitor.gender}</td>
                          <td className="px-5 py-3">{visitor.age}</td>
                          <td className="px-5 py-3">{visitor.duration}s</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="px-5 py-8 text-center text-gray-500">
                          No hay datos de visitantes recientes
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Renderizar el dashboard general de estadísticas
  return (
    <div className="bg-gray-50 min-h-screen py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Dashboard de Estadísticas</h1>
            <p className="text-gray-600">Analiza el rendimiento de tus propiedades</p>
          </div>
          <div className="flex gap-2">
            <button 
              onClick={handleExportData}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 text-sm font-medium"
            >
              <Download className="w-4 h-4" />
              Exportar
            </button>
            <button 
              onClick={() => fetchStatistics()}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 rounded-lg text-white hover:bg-blue-700 text-sm font-medium"
            >
              <RefreshCw className="w-4 h-4" />
              Actualizar
            </button>
          </div>
        </div>

        {/* Filter Options */}
        <div className="mb-6 flex flex-wrap items-center justify-between bg-white rounded-xl shadow-sm border border-gray-100 p-4">
          <div className="flex gap-2 mb-4 sm:mb-0">
            <TabButton 
              active={timeFilter === 'week'} 
              onClick={() => setTimeFilter('week')}
              icon={Calendar}
            >
              Última Semana
            </TabButton>
            <TabButton 
              active={timeFilter === 'month'} 
              onClick={() => setTimeFilter('month')}
              icon={Calendar}
            >
              Último Mes
            </TabButton>
            <TabButton 
              active={timeFilter === 'year'} 
              onClick={() => setTimeFilter('year')}
              icon={Calendar}
            >
              Último Año
            </TabButton>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setChartType('line')}
              className={`p-2 rounded-lg ${chartType === 'line' ? 'bg-blue-50 text-blue-600' : 'text-gray-500 hover:bg-gray-50'}`}
            >
              <TrendingUp className="w-5 h-5" />
            </button>
            <button
              onClick={() => setChartType('bar')}
              className={`p-2 rounded-lg ${chartType === 'bar' ? 'bg-blue-50 text-blue-600' : 'text-gray-500 hover:bg-gray-50'}`}
            >
              <BarChart2 className="w-5 h-5" />
            </button>
            <button
              onClick={() => setChartType('pie')}
              className={`p-2 rounded-lg ${chartType === 'pie' ? 'bg-blue-50 text-blue-600' : 'text-gray-500 hover:bg-gray-50'}`}
            >
              <PieChartIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
          <StatCard
            title="Visitas Totales"
            value="1,234"
            icon={Eye}
            trend={12}
            color="blue"
          />
          <StatCard
            title="Tiempo Promedio"
            value="3m 45s"
            icon={Clock}
            trend={-5}
            color="purple"
          />
          <StatCard
            title="Ubicaciones"
            value="8 ciudades"
            icon={GlobeIcon}
            trend={8}
            color="green"
          />
          <StatCard
            title="Tasa de Conversión"
            value="24.8%"
            icon={Percent}
            trend={15}
            color="amber"
          />
        </div>

        {/* Charts Section */}
<div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
  {/* Visitors Over Time */}
  <ChartCard 
    title="Visitas en el Tiempo" 
    icon={TrendingUp}
    tooltip="Muestra la evolución de las visitas a lo largo del tiempo."
  >
    <ResponsiveContainer width="100%" height={300}>
      {chartType === 'line' ? (
        <LineChart data={[
          { name: 'Ene', visits: 400 },
          { name: 'Feb', visits: 300 },
          { name: 'Mar', visits: 600 },
          { name: 'Abr', visits: 800 },
          { name: 'May', visits: 700 }
        ]}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f1f1f1" />
          <XAxis dataKey="name" stroke="#718096" />
          <YAxis stroke="#718096" />
          <Tooltip contentStyle={{ borderRadius: '8px' }} />
          <Legend />
          <Line 
            type="monotone" 
            dataKey="visits" 
            name="Visitas"
            stroke="#3B82F6" 
            strokeWidth={2}
            activeDot={{ r: 8 }}
            dot={{ fill: '#3B82F6', r: 4 }}
          />
        </LineChart>
      ) : chartType === 'bar' ? (
        <BarChart data={[
          { name: 'Ene', visits: 400 },
          { name: 'Feb', visits: 300 },
          { name: 'Mar', visits: 600 },
          { name: 'Abr', visits: 800 },
          { name: 'May', visits: 700 }
        ]}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f1f1f1" />
          <XAxis dataKey="name" stroke="#718096" />
          <YAxis stroke="#718096" />
          <Tooltip contentStyle={{ borderRadius: '8px' }} />
          <Legend />
          <Bar dataKey="visits" name="Visitas" fill="#3B82F6" radius={[4, 4, 0, 0]} />
        </BarChart>
      ) : (
        <AreaChart data={[
          { name: 'Ene', visits: 400 },
          { name: 'Feb', visits: 300 },
          { name: 'Mar', visits: 600 },
          { name: 'Abr', visits: 800 },
          { name: 'May', visits: 700 }
        ]}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f1f1f1" />
          <XAxis dataKey="name" stroke="#718096" />
          <YAxis stroke="#718096" />
          <Tooltip contentStyle={{ borderRadius: '8px' }} />
          <Legend />
          <defs>
            <linearGradient id="colorVisits" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.8}/>
              <stop offset="95%" stopColor="#3B82F6" stopOpacity={0.1}/>
            </linearGradient>
          </defs>
          <Area 
            type="monotone" 
            dataKey="visits" 
            name="Visitas"
            stroke="#3B82F6" 
            fillOpacity={1}
            fill="url(#colorVisits)" 
          />
        </AreaChart>
      )}
    </ResponsiveContainer>
  </ChartCard>

  {/* Visitor Demographics */}
  <ChartCard 
    title="Demografía de Visitantes" 
    icon={User}
    color="purple"
    tooltip="Muestra la distribución de edades de los visitantes interesados en tus propiedades."
  >
    <ResponsiveContainer width="100%" height={300}>
      <PieChart>
        <Pie
          data={[
            { name: '18-25', value: 400 },
            { name: '26-35', value: 300 },
            { name: '36-45', value: 300 },
            { name: '46+', value: 200 }
          ]}
          cx="50%"
          cy="50%"
          innerRadius={60}
          outerRadius={100}
          fill="#8884d8"
          dataKey="value"
          nameKey="name"
          labelLine={false}
          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
        >
          {[0,1,2,3].map((entry, index) => (
            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
          ))}
        </Pie>
        <Tooltip formatter={(value) => [`${value}`, 'Visitantes']} />
        <Legend layout="vertical" verticalAlign="middle" align="right" />
      </PieChart>
    </ResponsiveContainer>
  </ChartCard>

  {/* Property Performance */}
  <ChartCard 
    title="Rendimiento por Tipo de Propiedad" 
    icon={Building}
    color="green"
    tooltip="Muestra el rendimiento de tus propiedades según su tipo."
  >
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={[
        { name: 'Casas', visits: 400, clicks: 240 },
        { name: 'Departamentos', visits: 300, clicks: 139 },
        { name: 'Terrenos', visits: 200, clicks: 98 },
        { name: 'Locales', visits: 278, clicks: 120 }
      ]}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f1f1f1" />
        <XAxis dataKey="name" stroke="#718096" />
        <YAxis stroke="#718096" />
        <Tooltip contentStyle={{ borderRadius: '8px' }} />
        <Legend />
        <Bar dataKey="visits" name="Visitas" fill="#3B82F6" radius={[4, 4, 0, 0]} />
        <Bar dataKey="clicks" name="Clicks" fill="#8B5CF6" radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  </ChartCard>

  {/* Geographic Distribution */}
  <ChartCard 
    title="Distribución Geográfica" 
    icon={GlobeIcon}
    color="amber"
    tooltip="Muestra la distribución geográfica de los visitantes interesados en tus propiedades."
  >
    <ResponsiveContainer width="100%" height={300}>
      <BarChart layout="vertical" data={[
        { city: 'Lima', visits: 400 },
        { city: 'Arequipa', visits: 300 },
        { city: 'Trujillo', visits: 200 },
        { city: 'Cusco', visits: 278 },
        { city: 'Piura', visits: 189 }
      ]}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f1f1f1" horizontal={false} />
        <XAxis type="number" stroke="#718096" />
        <YAxis dataKey="city" type="category" width={100} stroke="#718096" />
        <Tooltip contentStyle={{ borderRadius: '8px' }} />
        <Bar dataKey="visits" name="Visitas" radius={[0, 4, 4, 0]}>
          {[0,1,2,3,4].map((entry, index) => (
            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  </ChartCard>
</div>

{/* Property Insights */}
<div className="mb-8">
  <ChartCard 
    title="Propiedades Más Visitadas" 
    icon={Building}
    tooltip="Muestra las propiedades que reciben mayor cantidad de visitas."
  >
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            <th className="px-5 py-3 border-b-2 border-gray-100">Propiedad</th>
            <th className="px-5 py-3 border-b-2 border-gray-100">Tipo</th>
            <th className="px-5 py-3 border-b-2 border-gray-100">Visitas</th>
            <th className="px-5 py-3 border-b-2 border-gray-100">Clicks</th>
            <th className="px-5 py-3 border-b-2 border-gray-100">Tiempo Promedio</th>
            <th className="px-5 py-3 border-b-2 border-gray-100">Acción</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 text-sm text-gray-700">
          {[1,2,3,4,5].map((_, index) => (
            <tr key={index} className="hover:bg-gray-50">
              <td className="px-5 py-3">Casa en San Isidro #{index + 1}</td>
              <td className="px-5 py-3">
                <span className="px-2 py-1 text-xs rounded-full bg-blue-50 text-blue-700">Casa</span>
              </td>
              <td className="px-5 py-3 font-medium">{200 - index * 30}</td>
              <td className="px-5 py-3">{150 - index * 20}</td>
              <td className="px-5 py-3">2m 30s</td>
              <td className="px-5 py-3">
                <button className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                  Ver Detalles
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </ChartCard>
</div>

{/* Device Distribution */}
<div className="mb-8">
  <ChartCard 
    title="Distribución por Dispositivos" 
    icon={Smartphone}
    color="indigo"
    tooltip="Muestra los diferentes dispositivos desde los que los usuarios visitan tus propiedades."
  >
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <div className="col-span-1">
        <ResponsiveContainer width="100%" height={200}>
          <PieChart>
            <Pie
              data={[
                { name: 'Móvil', value: 65 },
                { name: 'Desktop', value: 25 },
                { name: 'Tablet', value: 10 }
              ]}
              cx="50%"
              cy="50%"
              innerRadius={40}
              outerRadius={80}
              fill="#8884d8"
              dataKey="value"
              nameKey="name"
              labelLine={false}
              label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
            >
              <Cell fill="#3B82F6" />
              <Cell fill="#8B5CF6" />
              <Cell fill="#EC4899" />
            </Pie>
            <Tooltip formatter={(value) => [`${value}%`, '']} />
          </PieChart>
        </ResponsiveContainer>
      </div>

      <div className="col-span-2">
        <div className="h-full flex flex-col justify-center">
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-4 h-4 rounded-full bg-blue-500"></div>
              <div className="flex-1">
                <div className="flex justify-between mb-1">
                  <span className="text-sm font-medium text-gray-700">Móvil</span>
                  <span className="text-sm text-gray-600">65%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className="bg-blue-500 h-2 rounded-full" style={{ width: '65%' }}></div>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <div className="w-4 h-4 rounded-full bg-purple-500"></div>
              <div className="flex-1">
                <div className="flex justify-between mb-1">
                  <span className="text-sm font-medium text-gray-700">Desktop</span>
                  <span className="text-sm text-gray-600">25%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className="bg-purple-500 h-2 rounded-full" style={{ width: '25%' }}></div>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <div className="w-4 h-4 rounded-full bg-pink-500"></div>
              <div className="flex-1">
                <div className="flex justify-between mb-1">
                  <span className="text-sm font-medium text-gray-700">Tablet</span>
                  <span className="text-sm text-gray-600">10%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className="bg-pink-500 h-2 rounded-full" style={{ width: '10%' }}></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </ChartCard>
</div>

{/* Insights */}
<div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
  <ChartCard 
    title="Mejores Días para Visitas" 
    icon={Calendar}
    color="blue"
    tooltip="Muestra los días de la semana con mayor actividad de visitas."
  >
    <div className="h-40 flex flex-col justify-center items-center text-center">
      <div className="text-2xl font-bold text-blue-600 mb-2">Jueves y Viernes</div>
      <p className="text-gray-600">Son los días con mayor actividad en tus propiedades</p>
    </div>
  </ChartCard>
  
  <ChartCard 
    title="Horario Recomendado" 
    icon={Clock}
    color="green"
    tooltip="Muestra las horas del día con mayor actividad de usuarios."
  >
    <div className="h-40 flex flex-col justify-center items-center text-center">
      <div className="text-2xl font-bold text-green-600 mb-2">5:00 PM - 8:00 PM</div>
      <p className="text-gray-600">Es el horario con mayor actividad de visitantes</p>
    </div>
  </ChartCard>
</div>
      </div>
    </div>
  );
};

export default StatisticsPage;